import { useState, useContext, useEffect } from 'react';
import {
  QTypography,
  QThemeProvider,
  AnalysisRequestSearchBar,
  AnalysisRequestSearchResultsTable,
  AnalysisRequestSubmissionPanel,
  AnalysisRequestWrapper,
  QLinearProgress,
  validateStoreLinks,
} from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../shared/BackButton';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { fetchService } from '../../../utils/fetchService';
import { httpRequest } from '../../../utils/network.service';
import { createQueryString } from '../../../utils/createQueryString';
import { useQAsyncTablePagination } from '../../shared/useQAsyncTablePagination';
import { RequestAnalysisInfoContext } from './RequestAnalysisInfoContext';
import { useRequestAnalysisSubmissionQueue } from './useRequestAnalysisSubmissionQueue';

interface AppResult {
  categories: string[];
  description: string;
  iconUrl: string;
  minimumOsVersion: string;
  name: string;
  packageName: string;
  platform: string;
  price: number;
  storeUrl: string;
  vendorName: string;
  version: string;
}

export default function RequestAnalysisSearch() {
  const drawerWidth = 275;
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { enqueueSnackbar } = useSnackbar();
  const { submissionQueue, updateSubmissionQueue } = useRequestAnalysisSubmissionQueue();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [appResults, setAppResults] = useState([]);
  const [open, setOpen] = useState(!!submissionQueue.length);
  const [showNoResults, setShowNoResults] = useState(false);
  const navigate = useNavigate();

  const { platform, setPlatform, setNumberOfSubmissions, setSubmittedAt } = useContext(RequestAnalysisInfoContext);

  const { page, pageSize, pageSizeOptions, paginationOptions, setPaginationOptions, handleChangePage, handlePageSizeChange } =
    useQAsyncTablePagination();

  const handleRemove = ({ name, packageName, os }) => {
    const updatedQueue = submissionQueue.filter(app => !(app.name === name && app.packageName === packageName && app.os === os));
    updateSubmissionQueue(updatedQueue);
    if (!updatedQueue.length) {
      setOpen(false);
    }
  };

  const handleRemoveAll = () => {
    updateSubmissionQueue([]);
    setOpen(false);
  };

  const handleQueueSubmit = async () => {
    setNumberOfSubmissions(submissionQueue.length);

    const body = submissionQueue.map(({ packageName, version, os, name }) => ({
      state: {
        applicationVersion: {
          applicationId: packageName,
          version,
          os,
        },
        management: 'REQUESTED',
      },
      title: name,
    }));

    const { response, error } = await httpRequest({
      path: `${process.env.DEVICE_SERVICE_URL}/application/orgApp`,
      method: 'POST',
      body,
    });

    if (error) {
      console.error('Error submitting apps:', error);
      enqueueSnackbar(`Error submitting apps: ${submissionQueue.map(({ name }) => name).join(', ')}`, {
        variant: 'error',
      });
    } else if (response.status === 201) {
      updateSubmissionQueue([]);
      setSubmittedAt(new Date());
      navigate('/request-analysis/submission-confirmation');
    }
  };

  const checkAnalysisStatus = async (apps: AppResult[]) => {
    const { data, error } = await httpRequest({
      path: `${process.env.EVAL_SERVICE_URL}/eval/applications/os/${platform}/bulk-analysis-status`,
      method: 'POST',
      body: apps.map(({ packageName, version }) => ({ packageName, version })),
    });

    if (error) {
      console.error('Error fetching analysis status:', error);
    } else if (data.length) {
      const updatedResults = apps.map(result => {
        const match = data.find(analyzedApp => analyzedApp.packageName === result.packageName && analyzedApp.version === result.version);
        return match ? { ...result, status: match.status } : result;
      });
      setAppResults(updatedResults);
    }
    setLoading(false);
  };

  // look up apple app bundle id from url containing apple app store id
  const getAppleBundleId = async (url: string) => {
    const match = url.match(/id(\d+)/);
    if (match) {
      const appId = match[1];
      try {
        const response = await fetchService(`/application/itunes/lookup/${appId}`, accessToken);

        if (response) {
          const { bundleId } = response;
          return bundleId;
        }
      } catch (error) {
        console.error('Error fetching app data from Apple App Store:', error);
        enqueueSnackbar('Error fetching app data from Apple App Store', { variant: 'error' });
        setLoading(false);
      }
    }
    return null;
  };

  const handlePackageNameSearch = async (appId: string) => {
    const queryParamString = createQueryString({
      packageNames: [appId.toLowerCase()],
      os: platform,
    });
    try {
      const response = await fetchService(`/pdm/app-catalog/packages?${queryParamString}`, accessToken);

      if (response.length) {
        setAppResults(response);
        setPaginationOptions({
          total: response.length,
          lastPage: Math.ceil(response.length / pageSize),
          perPage: pageSize,
        });
        await checkAnalysisStatus(response);
      } else if (!response.length) {
        setLoading(false);
        setShowNoResults(true);
      }
    } catch (error) {
      console.error('Error during package name search:', error);
      enqueueSnackbar('Error during package name search. Please try again.', { variant: 'error' });
      setLoading(false);
      setShowNoResults(true);
    }
  };

  const extractPackageNameFromUrl = async (storeLink: string) => {
    const isValid = validateStoreLinks([storeLink], { accepted: [], rejected: [] });
    if (!isValid.accepted.length) {
      enqueueSnackbar('Invalid App Store link. Please provide a valid link.', { variant: 'error' });
      setLoading(false);
      return;
    }
    const url = isValid.accepted[0];
    const { hostname, searchParams } = new URL(url);
    if (hostname === 'apps.apple.com' || hostname === 'itunes.apple.com') {
      const bundleId = await getAppleBundleId(url);
      if (!bundleId) {
        enqueueSnackbar('Failed to retrieve bundle ID from Apple App Store link.', { variant: 'error' });
        setLoading(false);
        return;
      }
      await handlePackageNameSearch(bundleId);
    }

    if (hostname === 'play.google.com') {
      const packageName = searchParams.get('id');
      if (!packageName) {
        enqueueSnackbar('Failed to retrieve package name from Google Play Store link.', { variant: 'error' });
        setLoading(false);
        return;
      }
      await handlePackageNameSearch(packageName);
    }
  };

  const handleAppSearch = async () => {
    if (!searchQuery.length) return;
    const searchText = searchQuery.trim().toLowerCase();
    setAppResults([]);
    setShowNoResults(false);
    setLoading(true);
    // divert search queries containing store links to package name extraction + app metadata search
    if (searchText.includes('apps.apple.com') || searchText.includes('itunes.apple.com') || searchText.includes('play.google.com')) {
      await extractPackageNameFromUrl(searchText);
      return;
    }

    const queryParamString = createQueryString({
      searchText,
      os: platform,
      pageNum: String(page),
      pageSize: String(pageSize),
    });

    try {
      const response = await fetchService(`/pdm/app-catalog?${queryParamString}`, accessToken);
      if (response.results.length) {
        setAppResults(response.results);
        setPaginationOptions({
          total: response.totalResults,
          lastPage: response.numberOfPages,
          perPage: response.pageSize,
        });

        await checkAnalysisStatus(response.results);
      } else if (!response.results.length) {
        setLoading(false);
        setShowNoResults(true);
      }
    } catch (error) {
      console.error('Error during app search:', error);
      enqueueSnackbar('Error during app search. Please try again.', { variant: 'error' });
      setLoading(false);
      setShowNoResults(true);
    }
  };

  useEffect(() => {
    if (!searchQuery.length) {
      setAppResults([]);
      setPaginationOptions({
        total: 0,
        lastPage: 0,
        perPage: 0,
      });
    }
    handleAppSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, platform]);

  return (
    <QThemeProvider>
      <AnalysisRequestWrapper open={open} drawerWidth={drawerWidth}>
        <AnalysisRequestSubmissionPanel
          open={open}
          toggleDrawer={setOpen}
          submissionQueue={submissionQueue}
          handleRemove={handleRemove}
          handleRemoveAll={handleRemoveAll}
          handleQueueSubmit={handleQueueSubmit}
          drawerWidth={drawerWidth}
        />
        <BackButton href="/request-analysis" text="Back to Request Analysis" />
        <QTypography variant="h4Bold" sx={{ marginTop: '32px' }}>
          Search by App Name, Bundle ID, Vendor, or Store Link
        </QTypography>
        <AnalysisRequestSearchBar
          platform={platform}
          setPlatform={setPlatform}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleAppSearch={handleAppSearch}
        />
        {loading && searchQuery.length ? (
          <QLinearProgress color="primary" variant="indeterminate" />
        ) : (
          <AnalysisRequestSearchResultsTable
            submissionQueue={submissionQueue}
            updateSubmissionQueue={updateSubmissionQueue}
            setOpen={setOpen}
            platform={platform}
            page={page}
            pageSize={pageSize > paginationOptions.total ? paginationOptions.total : pageSize}
            pageSizeOptions={pageSizeOptions}
            paginationOptions={paginationOptions}
            handleChangePage={handleChangePage}
            handlePageSizeChange={handlePageSizeChange}
            appResults={appResults}
            showNoResults={showNoResults}
          />
        )}
      </AnalysisRequestWrapper>
    </QThemeProvider>
  );
}
